import { Component, computed, input, output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { NgxTolgeeModule, TranslateService } from '@tolgee/ngx';
import { IAiAssetAnalysisResponse } from '@app/shared/services/image-analysis.service';

@Component({
	selector: 'au-ai-floating-confirmation',
	standalone: true,
	imports: [
		CommonModule,
		MatButtonModule,
		MatIconModule,
		MatExpansionModule,
		NgxTolgeeModule,
	],
	templateUrl: './ai-floating-confirmation.component.html',
	styleUrls: ['./ai-floating-confirmation.component.scss'],
})
export class AiFloatingConfirmationComponent {
	accept = output<void>();
	decline = output<void>();

	private readonly translateService = inject(TranslateService);

	// New input for AI analysis response data
	aiAnalysisResponse = input<IAiAssetAnalysisResponse | null>(null);

	// Create a type-safe field selector to avoid hardcoded strings
	private createFieldSelector<T>(): Record<keyof T, keyof T> {
		return new Proxy({} as Record<keyof T, keyof T>, {
			get: (target, prop) => prop as keyof T,
		});
	}

	private readonly fields = this.createFieldSelector<IAiAssetAnalysisResponse>();

	private readonly excludeFields: (keyof IAiAssetAnalysisResponse)[] = [
		this.fields.building_component_table_code,
		this.fields.labels,
		this.fields.data_sheet_url,
	];

	// Computed property to get formatted field names
	filteredSuggestions = computed(() => {
		const response = this.aiAnalysisResponse();
		if (!response) return [];

		const suggestions: string[] = [];

		// Get all fields dynamically from the interface
		const fieldsToCheck = Object.keys(response) as (keyof IAiAssetAnalysisResponse)[];

		// Check each field and add formatted name if it has a value and is not excluded
		fieldsToCheck.forEach(field => {
			if (!this.excludeFields.includes(field)) {
				const value = response[field];
				if (this.hasValue(value)) {
					suggestions.push(this.formatFieldName(field as string));
				}
			}
		});

		return suggestions;
	});

	// Computed property to get the count of suggestions
	suggestionsCount = computed(() => this.filteredSuggestions().length);

	onAccept(): void {
		this.accept.emit();
	}

	onDecline(): void {
		this.decline.emit();
	}

	private hasValue(value: unknown): boolean {
		if (value === null || value === undefined) return false;
		if (typeof value === 'string' && value.trim() === '') return false;
		if (Array.isArray(value) && value.length === 0) return false;
		if (
			typeof value === 'object' &&
			!Array.isArray(value) &&
			Object.keys(value).length === 0
		)
			return false;
		return true;
	}

	private formatFieldName(fieldName: string): string {
		return this.translateService.instant(`ai_suggestion_overview_${fieldName}`);
	}
}
